import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Trash2, RefreshCw, Package, Search, Settings, CheckCircle, ExternalLink, Lock, Eye, EyeOff, LogOut, KeyRound } from 'lucide-react';
import { getOrders, updateOrderStatus, deleteOrder, downloadOrdersExcel, type StoredOrder } from '@/lib/orders';
import { getConfig, saveConfig } from '@/lib/config';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const PASSWORD_KEY = 'dosdetres_admin_pwd';
const SESSION_KEY = 'dosdetres_admin_session';
const DEFAULT_PASSWORD = 'dosdetres23';

function getStoredPassword(): string {
  return localStorage.getItem(PASSWORD_KEY) || DEFAULT_PASSWORD;
}

function LoginScreen({ onLogin }: { onLogin: () => void }) {
  const [pwd, setPwd] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (pwd === getStoredPassword()) {
      sessionStorage.setItem(SESSION_KEY, '1');
      onLogin();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setTimeout(() => setError(false), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 250, damping: 35 }}
        className="w-full max-w-sm"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <span className="text-6xl font-black text-primary" style={{ letterSpacing: '-4px' }}>23</span>
          <p className="text-xs font-black tracking-widest text-foreground uppercase mt-1">DOS DE TRES</p>
          <p className="text-[10px] text-muted-foreground tracking-widest uppercase mt-1">Panel Administrativo</p>
        </div>

        <motion.form
          onSubmit={handleLogin}
          animate={shake ? { x: [-8, 8, -6, 6, -4, 4, 0] } : { x: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-card border border-border rounded-xl p-8 space-y-5"
        >
          <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mx-auto mb-2">
            <Lock className="text-primary" size={22} />
          </div>
          <h2 className="text-center text-sm font-black tracking-widest uppercase text-foreground">Acceso restringido</h2>

          <div className="space-y-2">
            <label className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground">Contraseña</label>
            <div className="relative">
              <Input
                type={show ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="••••••••••"
                className={`bg-background pr-10 font-mono ${error ? 'border-destructive' : 'border-border focus:border-primary'}`}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {error && <p className="text-destructive text-xs font-bold">Contraseña incorrecta. Intenta de nuevo.</p>}
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-primary text-primary-foreground font-black text-xs tracking-widest uppercase rounded hover:bg-primary/90 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            ENTRAR AL PANEL
          </button>
        </motion.form>
      </motion.div>
    </div>
  );
}

const STATUS_COLORS: Record<StoredOrder['status'], string> = {
  'Nuevo':      'bg-primary/20 text-primary border-primary/30',
  'En proceso': 'bg-chart-5/20 text-chart-5 border-chart-5/30',
  'Entregado':  'bg-green-500/20 text-green-400 border-green-500/30',
};

export default function Admin() {
  const [authenticated, setAuthenticated] = useState(false);
  const [orders, setOrders] = useState<StoredOrder[]>([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('Todos');
  const [showConfig, setShowConfig] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [configSaved, setConfigSaved] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdMsg, setPwdMsg] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === '1') setAuthenticated(true);
  }, []);

  const handleChangePwd = () => {
    if (newPwd.length < 6) { setPwdMsg('❌ Mínimo 6 caracteres'); return; }
    if (newPwd !== confirmPwd) { setPwdMsg('❌ Las contraseñas no coinciden'); return; }
    localStorage.setItem(PASSWORD_KEY, newPwd);
    setNewPwd(''); setConfirmPwd('');
    setPwdMsg('✅ Contraseña actualizada correctamente');
    setTimeout(() => setPwdMsg(''), 3000);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setAuthenticated(false);
  };

  if (!authenticated) return <LoginScreen onLogin={() => setAuthenticated(true)} />;

  const reload = () => setOrders(getOrders());

  useEffect(() => {
    reload();
    setWebhookUrl(getConfig().sheetsWebhookUrl || '');
  }, []);

  const handleSaveConfig = () => {
    saveConfig({ sheetsWebhookUrl: webhookUrl.trim() });
    setConfigSaved(true);
    setTimeout(() => setConfigSaved(false), 3000);
  };

  const filtered = orders.filter((o) => {
    const matchSearch =
      o.folio.toLowerCase().includes(search.toLowerCase()) ||
      o.clientName.toLowerCase().includes(search.toLowerCase()) ||
      o.teamName.toLowerCase().includes(search.toLowerCase()) ||
      o.phone.includes(search);
    const matchStatus = filterStatus === 'Todos' || o.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const handleStatus = (folio: string, status: StoredOrder['status']) => {
    updateOrderStatus(folio, status);
    reload();
  };

  const handleDelete = (folio: string) => {
    if (confirm(`¿Eliminar pedido ${folio}?`)) {
      deleteOrder(folio);
      reload();
    }
  };

  const stats = {
    total: orders.length,
    nuevo: orders.filter((o) => o.status === 'Nuevo').length,
    enProceso: orders.filter((o) => o.status === 'En proceso').length,
    entregado: orders.filter((o) => o.status === 'Entregado').length,
  };

  return (
    <div className="min-h-screen bg-background py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
        >
          <div>
            <p className="text-primary font-bold text-xs tracking-widest uppercase mb-1">PANEL ADMINISTRATIVO</p>
            <h1 className="text-3xl font-black uppercase tracking-tight text-foreground">
              GESTIÓN DE <span className="text-primary">PEDIDOS</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-card border border-border text-muted-foreground text-xs font-bold tracking-widest rounded hover:border-destructive hover:text-destructive transition-all"
            >
              <LogOut size={14} /> SALIR
            </button>
            <button
              onClick={() => setShowConfig(!showConfig)}
              className={`flex items-center gap-2 px-4 py-2 border text-xs font-bold tracking-widest rounded transition-all ${showConfig ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:border-primary hover:text-primary'}`}
            >
              <Settings size={14} /> CONFIGURAR SHEETS
            </button>
            <button
              onClick={reload}
              className="flex items-center gap-2 px-4 py-2 bg-card border border-border text-muted-foreground text-xs font-bold tracking-widest rounded hover:border-primary hover:text-primary transition-all"
            >
              <RefreshCw size={14} /> ACTUALIZAR
            </button>
            <button
              onClick={() => downloadOrdersExcel(filtered)}
              disabled={filtered.length === 0}
              className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground text-xs font-black tracking-widest rounded hover:bg-primary/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download size={14} /> DESCARGAR EXCEL
            </button>
          </div>
        </motion.div>

        {/* Panel configuración Google Sheets */}
        {showConfig && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-primary/40 rounded-xl p-6 mb-8"
          >
            <h3 className="text-sm font-black uppercase tracking-widest text-primary mb-4 flex items-center gap-2">
              <Settings size={16} /> CONEXIÓN CON GOOGLE SHEETS
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Instrucciones */}
              <div className="space-y-3">
                <p className="text-xs font-bold text-foreground tracking-wider uppercase">Pasos para configurar:</p>
                <ol className="space-y-2 text-xs text-muted-foreground">
                  <li className="flex gap-2"><span className="text-primary font-black shrink-0">1.</span> Abre tu Google Sheet y ve a <strong className="text-foreground">Extensiones → Apps Script</strong></li>
                  <li className="flex gap-2"><span className="text-primary font-black shrink-0">2.</span> Borra todo el código existente y pega el script de abajo</li>
                  <li className="flex gap-2"><span className="text-primary font-black shrink-0">3.</span> Haz clic en <strong className="text-foreground">Implementar → Nueva implementación</strong></li>
                  <li className="flex gap-2"><span className="text-primary font-black shrink-0">4.</span> Tipo: <strong className="text-foreground">Aplicación web</strong> · Acceso: <strong className="text-foreground">Cualquier persona</strong></li>
                  <li className="flex gap-2"><span className="text-primary font-black shrink-0">5.</span> Copia la URL que te genera y pégala aquí abajo</li>
                </ol>
                <a
                  href="https://script.google.com"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary font-bold hover:underline mt-1"
                >
                  <ExternalLink size={12} /> Abrir Google Apps Script
                </a>
              </div>
              {/* Script a copiar */}
              <div>
                <p className="text-xs font-bold text-foreground tracking-wider uppercase mb-2">Script a pegar en Apps Script:</p>
                <pre className="bg-background border border-border rounded-lg p-3 text-[10px] text-muted-foreground overflow-auto max-h-40 font-mono leading-relaxed select-all">{`function doPost(e) {
  try {
    var data = JSON.parse(e.postData.contents);
    var sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
    if (sheet.getLastRow() === 0) {
      sheet.appendRow(['FOLIO','FECHA','HORA','ESTADO',
        'CLIENTE','TELÉFONO','EMAIL','CIUDAD','ESTADO',
        'DEPORTE','EQUIPO','CANTIDAD','ESTILO','PRECIO',
        'COLORES','LOGO','DESC.LOGO','NÚMEROS','NOMBRES',
        'TALLAS','F.ENTREGA','REFERENCIA','NOTAS']);
    }
    sheet.appendRow([data.folio,data.fecha,data.hora,
      data.status,data.clientName,data.phone,data.email,
      data.city,data.state,data.sport,data.teamName,
      data.quantity,data.shirtStyle,data.estimatedPrice,
      data.colors,data.hasLogo,data.logoDescription,
      data.hasNumbers,data.hasNames,data.sizes,
      data.deliveryDate,data.referenceDescription,
      data.additionalNotes]);
    return ContentService
      .createTextOutput(JSON.stringify({ok:true}))
      .setMimeType(ContentService.MimeType.JSON);
  } catch(err) {
    return ContentService
      .createTextOutput(JSON.stringify({ok:false,e:err.toString()}))
      .setMimeType(ContentService.MimeType.JSON);
  }
}
function doGet() {
  return ContentService.createTextOutput('API activa ✅');
}`}</pre>
              </div>
            </div>
            {/* Input URL */}
            <div className="mt-5 pt-5 border-t border-border">
              <p className="text-xs font-bold text-foreground tracking-wider uppercase mb-2">URL de tu Apps Script Web App:</p>
              <div className="flex gap-3">
                <Input
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://script.google.com/macros/s/XXXXXXX/exec"
                  className="bg-background border-border font-mono text-xs flex-1"
                />
                <button
                  onClick={handleSaveConfig}
                  className={`flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest rounded transition-all shrink-0 ${configSaved ? 'bg-green-500 text-white' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}
                >
                  {configSaved ? <><CheckCircle size={14} /> ¡GUARDADO!</> : 'GUARDAR URL'}
                </button>
              </div>
              {webhookUrl && (
                <p className="text-[10px] text-green-400 mt-2 flex items-center gap-1">
                  <CheckCircle size={11} /> Conectado — cada nuevo pedido se enviará automáticamente a tu Google Sheet.
                </p>
              )}
              {!webhookUrl && (
                <p className="text-[10px] text-muted-foreground mt-2">Sin URL configurada — los pedidos solo se guardan localmente.</p>
              )}
            </div>

            {/* Cambio de contraseña */}
            <div className="mt-5 pt-5 border-t border-border">
              <p className="text-xs font-bold text-foreground tracking-wider uppercase mb-3 flex items-center gap-2">
                <KeyRound size={14} className="text-primary" /> CAMBIAR CONTRASEÑA
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  type="password"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  placeholder="Nueva contraseña"
                  className="bg-background border-border font-mono text-xs"
                />
                <Input
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  placeholder="Confirmar contraseña"
                  className="bg-background border-border font-mono text-xs"
                />
                <button
                  onClick={handleChangePwd}
                  className="px-4 py-2 bg-card border border-border text-xs font-black tracking-widest rounded hover:border-primary hover:text-primary transition-all"
                >
                  ACTUALIZAR
                </button>
              </div>
              {pwdMsg && <p className="text-xs mt-2 font-bold">{pwdMsg}</p>}
              <p className="text-[10px] text-muted-foreground mt-2">Contraseña actual por defecto: <span className="font-mono text-primary">dosdetres23</span></p>
            </div>
          </motion.div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'TOTAL PEDIDOS', value: stats.total, color: 'text-foreground' },
            { label: 'NUEVOS', value: stats.nuevo, color: 'text-primary' },
            { label: 'EN PROCESO', value: stats.enProceso, color: 'text-chart-5' },
            { label: 'ENTREGADOS', value: stats.entregado, color: 'text-green-400' },
          ].map((s) => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-5 text-center">
              <p className={`text-3xl font-black font-mono ${s.color}`}>{s.value}</p>
              <p className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por folio, cliente, equipo o teléfono..."
              className="pl-9 bg-card border-border"
            />
          </div>
          <Select onValueChange={setFilterStatus} defaultValue="Todos">
            <SelectTrigger className="w-full md:w-44 bg-card border-border">
              <SelectValue placeholder="Estado..." />
            </SelectTrigger>
            <SelectContent>
              {['Todos', 'Nuevo', 'En proceso', 'Entregado'].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Orders Table */}
        {filtered.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-16 text-center">
            <Package className="text-muted-foreground mx-auto mb-4" size={40} />
            <p className="text-muted-foreground font-bold text-lg">
              {orders.length === 0 ? 'Aún no hay pedidos registrados.' : 'No se encontraron resultados.'}
            </p>
            {orders.length === 0 && (
              <p className="text-muted-foreground text-sm mt-2">Los pedidos aparecerán aquí cuando un cliente complete el formulario.</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((order, i) => (
              <motion.div
                key={order.folio}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  {/* Folio + fecha */}
                  <div className="shrink-0 min-w-[160px]">
                    <p className="font-black font-mono text-primary text-base">{order.folio}</p>
                    <p className="text-[11px] text-muted-foreground">{order.fecha} {order.hora}</p>
                  </div>

                  {/* Info del pedido */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-[10px] text-muted-foreground font-bold tracking-widest uppercase mb-1">CLIENTE</p>
                      <p className="font-bold text-foreground">{order.clientName}</p>
                      <p className="text-muted-foreground font-mono text-xs">{order.phone}</p>
                      <p className="text-muted-foreground text-xs">{order.city}, {order.state}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground font-bold tracking-widest uppercase mb-1">PEDIDO</p>
                      <p className="font-bold text-foreground">{order.teamName}</p>
                      <p className="text-muted-foreground text-xs">{order.sport}</p>
                      <p className="text-muted-foreground text-xs">{order.quantity} pzs · {order.shirtStyle}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground font-bold tracking-widest uppercase mb-1">PRECIO EST.</p>
                      <p className="font-black text-primary font-mono">{order.estimatedPrice}</p>
                      <p className="text-muted-foreground text-xs mt-1">Tallas: {order.sizes.slice(0, 40)}{order.sizes.length > 40 ? '...' : ''}</p>
                      {/* Logo adjunto */}
                      {order.logoImageBase64 && (
                        <div className="mt-2">
                          <p className="text-[10px] text-muted-foreground font-bold tracking-widest uppercase mb-1">LOGO ADJUNTO</p>
                          <a href={order.logoImageBase64} download={`logo-${order.folio}.png`}>
                            <img
                              src={order.logoImageBase64}
                              alt="Logo"
                              className="h-14 w-auto object-contain rounded border border-border hover:border-primary transition-colors cursor-pointer"
                              title="Clic para descargar"
                            />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex flex-row md:flex-col items-center gap-2 shrink-0">
                    <Select
                      defaultValue={order.status}
                      onValueChange={(v) => handleStatus(order.folio, v as StoredOrder['status'])}
                    >
                      <SelectTrigger className={`text-[10px] font-black tracking-wider border rounded px-2 py-1 h-auto w-36 ${STATUS_COLORS[order.status]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Nuevo">Nuevo</SelectItem>
                        <SelectItem value="En proceso">En proceso</SelectItem>
                        <SelectItem value="Entregado">Entregado</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      onClick={() => handleDelete(order.folio)}
                      className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                      title="Eliminar pedido"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {/* Notas si hay */}
                {order.additionalNotes && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-[10px] text-muted-foreground font-bold tracking-widest uppercase mb-1">NOTAS</p>
                    <p className="text-xs text-muted-foreground">{order.additionalNotes}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        <p className="text-center text-[10px] text-muted-foreground mt-8 font-mono">
          Acceso: {window.location.origin}/#/admin · Los datos se guardan en este navegador.
        </p>
      </div>
    </div>
  );
}
